import React, { useState, useEffect } from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  Drawer,
  CssBaseline,
  AppBar as MuiAppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Button,
  useMediaQuery,
} from "@mui/material";

import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Inbox as InboxIcon,
  Mail as MailIcon,
} from "@mui/icons-material";

import { Outlet, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import SideMenu from "../Components/SideMenu";
import Spinner from "../Components/Spinner";

const drawerWidth = 300;

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  })
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: `${drawerWidth}px`,
    transition: theme.transitions.create(["margin", "width"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

export default function DrawerLayout() {
  const navigate = useNavigate();
  const profile = useSelector((state) => state.profileReducer.profile);
  const isLoading = useSelector((state) => state.dataReducer.isLoading);
  const getName = localStorage.getItem("name");
  const getUsername = localStorage.getItem("username");
  const theme = useTheme();
  const [open, setOpen] = React.useState(false);
  const [anchorElUser, setAnchorElUser] = useState(null);

  // Check if it's mobile screen
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerOpen = () => {
    setOpen(!open);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  // Auto-hide drawer when clicking outside on mobile
  useEffect(() => {
    if (isMobile && open) {
      const handleClickOutside = (event) => {
        const drawer = document.querySelector('.MuiDrawer-paper');
        const hamburgerBtn = event.target.closest('[aria-label="open drawer"]');
        
        // If click is outside drawer and not on hamburger button
        if (drawer && !drawer.contains(event.target) && !hamburgerBtn) {
          setOpen(false);
        }
      };

      // Add event listener with small delay
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isMobile, open]);

  // Auto-hide drawer when clicking outside on desktop
  useEffect(() => {
    if (!isMobile && open) {
      const handleClickOutside = (event) => {
        const drawer = document.querySelector('.MuiDrawer-paper');
        const hamburgerBtn = event.target.closest('[aria-label="open drawer"]');
        const appBar = document.querySelector('.MuiAppBar-root');
        
        // If click is outside drawer and not on hamburger button or app bar
        if (drawer && !drawer.contains(event.target) && !hamburgerBtn && !appBar?.contains(event.target)) {
          setOpen(false);
        }
      };

      // Add event listener with small delay
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [isMobile, open]);

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  return (
    <Box sx={{ display: "flex" }}>
      {/* <CssBaseline /> */}
      <AppBar position="fixed" open={open} sx={{ backgroundColor: "#283042" }}>
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box display="flex" alignItems="center">
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              edge="start"
              sx={{ mr: 2 }}
              // sx={{ mr: 2, ...(open && { display: "none" }) }}
            >
              <MenuIcon />
            </IconButton>
            <span className="d-none d-md-block">
              <Typography
                variant="subtitle1"
                noWrap
                component="div"
                color="#f1f3f4"
              >
                {/* Internship and Cooperative Education */}
                BUU|ENG ระบบฝึกงานและสหกิจศึกษา
              </Typography>
            </span>
          </Box>
          {profile ? (
            <Box display="flex" alignItems="center">
              <Typography variant="subtitle1" color="inherit" m={1}>
                {/* ยินดีต้อนรับ {profile?.name ?? ""} */}
                ยินดีต้อนรับ {getName ?? ""}
              </Typography>

              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar
                  alt="profile"
                  sx={{ width: 56, height: 56 }}
                  src={
                    profile && profile.m_role === "staff"
                      ? profile.pic_url //"./assets/images/user.png"
                      : `https://reg.buu.ac.th/registrar/getstudentimage.asp?id=${getUsername}`
                  }
                />
              </IconButton>
              <Menu
                sx={{ mt: "50px" }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem
                  onClick={() => {
                    localStorage.setItem("username", "");
                    localStorage.setItem("name", "");
                    navigate("/login");
                  }}
                >
                  <Typography textAlign="center">Logout</Typography>

                </MenuItem>
                {profile?.b_id == 0 && (
 
                  <MenuItem
                    onClick={() => {
                      navigate("/admin-settings");
                    }}

                  >
                    <Typography textAlign="center">ตั้งค่า (Admin)</Typography>

                  </MenuItem>
                )}

              </Menu>
            </Box>
          ) : (
            <Button color="inherit" onClick={() => navigate("/login")}>
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            backgroundColor: "#283042",
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <SideMenu />
      </Drawer>
      <Main
        open={open}
        sx={{ background: "rgba(245,244,245,0.15)", width: "100vw" }}
      >
        {isLoading && <Spinner />}
        <DrawerHeader />
        <Outlet />
      </Main>
    </Box>
  );
}
